// Terouma Poche — helpers partagés par les 4 écrans téléphone.
//
// Ici : le brouillon (texte dicté + lignes en cours de correction, gardé sur
// le téléphone, un par compte et par synagogue) et sa reprise à l'ouverture
// de l'app, le jour local, les libellés des modes de paiement et les petites
// fonctions de forme (segmentation des phrases, validation d'une ligne,
// charge utile de /poche/save). Aucun appel réseau ici.
//
// Champs du brouillon (`terouma_poche_draft:<compte>:<synagogue>`) :
//   text, parsed, items, unparsed, parsed_at   dictée et lignes en cours
//   handled_unparsed                           « pas compris » déjà traités
//   default_fete_id, default_fete_manual       fête du moment envoyée / choisie à la main
//   engine, ai_cost_cents                      comment la dictée a été comprise (IA / règles)
//   complements, complement_seq, complement_text, extra_unparsed
//                                              dons oubliés ajoutés depuis la vérification
//   screen, left                               écran de reprise (« dicter » / « verifier »),
//                                              sortie par la croix (plus de reprise imposée)
//   saving_at                                  enregistrement parti, réponse pas encore reçue
import { toast } from "sonner";
import { getImpersonatedOrgId } from "./api";
import { orderFetes } from "./fetes";

// Racine des clés de brouillon ; seule, c'est l'ancien brouillon de session
// (avant la reprise à l'ouverture), repris une fois puis effacé.
export const DRAFT_KEY = "terouma_poche_draft";

// ── Modes de paiement (codes du contrat API → libellés français) ───────────
export const METHOD_LABELS = {
  cash: "Espèces",
  check: "Chèque",
  transfer: "Virement",
  card: "Carte",
  other: "Autre",
};

// Segment « Payé ? » de l'écran Vérification : Promis puis les modes.
export const PAID_SEGMENTS = [
  { value: "promised", label: "Promis" },
  { value: "cash", label: "Espèces" },
  { value: "check", label: "Chèque" },
  { value: "transfer", label: "Virement" },
  { value: "card", label: "Carte" },
];

// Mini-feuille « Payé » de l'écran Retrouver.
export const PAY_METHODS = ["cash", "check", "transfer", "card", "other"];

// ── Jour local (pas UTC) : à 0h30 à Paris, on est bien le nouveau jour ─────
const pad2 = (n) => String(n).padStart(2, "0");
export function todayLocalISO(d = new Date()) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

// « Lundi 5 octobre » pour l'en-tête de l'accueil.
export function longDateLabel(d = new Date()) {
  const s = d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// « 04/10 » pour les petites notes de date.
export function shortDateLabel(iso) {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return "";
  return `${m[3]}/${m[2]}`;
}

// ── Brouillon ─────────────────────────────────────────────────────────────
// Gardé sur le téléphone (localStorage) : fermer l'app ne perd rien, et elle
// rouvre la dictée en cours. Un brouillon par compte ET par synagogue : l'owner
// qui passe de Perreux à Nogent ne voit jamais le brouillon de l'autre. Il
// contient des noms et des montants de donateurs : périmé après 7 jours sans
// modification, effacé à la déconnexion (`clearAllDrafts`) et dès qu'un autre
// compte ouvre la Poche sur ce téléphone. Stockage plein ou bloqué : l'onglet
// le garde au moins le temps de la visite (sessionStorage, comme avant).
// Chaque fonction prend la clé en premier : un écran la fixe à son ouverture
// (`draftKeyFor`), et une réponse tardive (transcription, analyse) arrivée
// après un changement de synagogue écrit toujours dans le bon brouillon.
const DRAFT_PREFIX = `${DRAFT_KEY}:`;
export const DRAFT_MAX_AGE_MS = 7 * 24 * 3600 * 1000;
const DRAFT_STORES = ["localStorage", "sessionStorage"];

const isDraftKey = (key) => typeof key === "string" && key.startsWith(DRAFT_PREFIX);
const isDraftObject = (d) => !!d && typeof d === "object" && !Array.isArray(d);
// Sans date lisible : périmé (tout brouillon écrit ici porte `updated_at`).
const draftExpired = (d) => {
  const at = Date.parse(d?.updated_at || "");
  return !Number.isFinite(at) || Date.now() - at > DRAFT_MAX_AGE_MS;
};

// L'accès même à window.localStorage peut lever (stockage bloqué, Safari privé).
function webStore(name) {
  try {
    return window[name] || null;
  } catch {
    return null;
  }
}

function readStored(name, key) {
  try {
    const raw = webStore(name)?.getItem(key);
    const d = raw ? JSON.parse(raw) : null;
    return isDraftObject(d) ? d : null;
  } catch {
    return null;
  }
}

function removeStored(name, key) {
  try {
    webStore(name)?.removeItem(key);
  } catch {
    /* stockage indisponible : rien à effacer */
  }
}

// Toutes les clés de brouillon d'un stockage (liste copiée : on peut effacer en la parcourant).
function storedDraftKeys(name) {
  const keys = [];
  try {
    const st = webStore(name);
    for (let i = 0; st && i < st.length; i += 1) {
      const k = st.key(i);
      if (isDraftKey(k)) keys.push(k);
    }
  } catch {
    /* stockage indisponible */
  }
  return keys;
}

// Téléphone d'abord ; s'il refuse (plein, bloqué), l'onglet. La copie de
// l'autre stockage est retirée : jamais une version plus ancienne relue.
function writeStored(key, draft) {
  const json = JSON.stringify(draft);
  try {
    webStore("localStorage").setItem(key, json);
    removeStored("sessionStorage", key);
    return true;
  } catch {
    removeStored("localStorage", key);
    try {
      webStore("sessionStorage").setItem(key, json);
      return true;
    } catch {
      return false;
    }
  }
}

// Ménage, une fois par compte et par chargement de page : brouillons périmés
// ou d'un autre compte effacés, ancien brouillon (sessionStorage, sans compte
// ni synagogue) repris une fois — jamais pour l'owner : rien ne dit de quelle
// synagogue il vient.
let tidiedFor = null;
function tidyDrafts(user, key) {
  const userId = String(user.id);
  if (tidiedFor === userId) return;
  tidiedFor = userId;
  const mine = `${DRAFT_PREFIX}${userId}:`;
  DRAFT_STORES.forEach((name) => {
    storedDraftKeys(name).forEach((k) => {
      const d = k.startsWith(mine) ? readStored(name, k) : null;
      if (!d || draftExpired(d)) removeStored(name, k);
    });
  });
  const legacy = readStored("sessionStorage", DRAFT_KEY);
  if (legacy && user.role !== "owner" && !draftExpired(legacy) && !loadDraft(key)) {
    writeStored(key, legacy);
  }
  removeStored("sessionStorage", DRAFT_KEY);
}

// Clé du brouillon de ce compte dans la synagogue ouverte (celle de l'owner
// qui la gère, sinon celle du compte). À fixer à l'ouverture d'un écran.
// Seul l'owner gère une autre synagogue (le serveur ramène admin et manager
// à la leur, scope.py) : une marque « owner gère X » oubliée sur un téléphone
// partagé ne doit pas ranger le brouillon d'un admin sous X.
function openOrgId(user) {
  let impersonated = null;
  try {
    impersonated = user.role === "owner" ? getImpersonatedOrgId() : null;
  } catch {
    /* stockage bloqué : synagogue du compte */
  }
  return impersonated || user.organization_id || "-";
}

export function draftKeyFor(user) {
  if (!user?.id) return null;
  const orgId = openOrgId(user);
  const key = `${DRAFT_PREFIX}${user.id}:${orgId}`;
  tidyDrafts(user, key);
  return key;
}

export function loadDraft(key) {
  if (!isDraftKey(key)) return null;
  for (const name of DRAFT_STORES) {
    const d = readStored(name, key);
    if (!d) continue;
    if (!draftExpired(d)) return d;
    removeStored(name, key);
  }
  return null;
}

export function saveDraft(key, patch) {
  if (!isDraftKey(key)) return null;
  const next = { ...(loadDraft(key) || {}), ...patch, updated_at: new Date().toISOString() };
  return writeStored(key, next) ? next : null;
}

export function clearDraft(key) {
  if (!isDraftKey(key)) return;
  DRAFT_STORES.forEach((name) => removeStored(name, key));
}

// Déconnexion : plus aucun brouillon (ni nom ni montant) sur ce téléphone.
export function clearAllDrafts() {
  DRAFT_STORES.forEach((name) => storedDraftKeys(name).forEach((k) => removeStored(name, k)));
  removeStored("sessionStorage", DRAFT_KEY);
}

// ── Reprise de la dictée à l'ouverture de l'app ───────────────────────────
// Tant qu'une dictée n'est pas finie, l'app se rouvre dessus. Elle est finie
// quand on la quitte : enregistrée ou « Effacer » (brouillon effacé), ou
// fermée par la croix (`left` : le brouillon est gardé, l'accueil propose de
// le reprendre, mais l'app ne l'impose plus). Revenir dans la dictée
// (`markDraftScreen`) relance la reprise.
// Contenu = ce qui peut devenir un don. Un passage raté seul (`voice_failures`,
// de simples minutes) n'en est pas : au clavier (micro coupé ou refusé), l'écran
// rouvert serait vide, sans « Effacer » possible, et l'app y reviendrait sans
// fin. La bannière reste visible en rouvrant la dictée au micro.
export function draftHasContent(d) {
  if (!isDraftObject(d)) return false;
  const filled = (v) => Array.isArray(v) && v.length > 0;
  return !!String(d.text || "").trim() || filled(d.items) || filled(d.unparsed)
    || !!String(d.complement_text || "").trim();
}

export function draftResumePath(d) {
  return d?.screen === "verifier" && Array.isArray(d.items) ? "/poche/verifier" : "/poche/dicter";
}

// Écran de la dictée ouvert : c'est là que l'app reviendra. Rien n'est créé
// sans brouillon (le premier mot le créera).
export function markDraftScreen(key, screen) {
  const d = loadDraft(key);
  if (d && (d.screen !== screen || d.left)) saveDraft(key, { screen, left: false });
}

// Croix de la dictée : on sort, le brouillon reste (et l'accueil le propose).
export function leaveDraft(key) {
  if (loadDraft(key)) saveDraft(key, { left: true });
}

// Ouverture de l'app = chargement de la page : la reprise automatique ne se
// fait qu'à ce moment-là. Revenir soi-même à l'accueil pendant la visite n'y
// renvoie jamais.
let pocheOpened = false;
export function markPocheOpened() {
  pocheOpened = true;
}

export function resumePathAtOpening(key) {
  if (pocheOpened) return null;
  const d = loadDraft(key);
  return draftHasContent(d) && !d.left ? draftResumePath(d) : null;
}

// ── Découpage local des phrases (même règle que le serveur, pour le compteur) ─
export function splitPhrases(text) {
  if (!text) return [];
  return text
    .replace(/\r/g, "")
    .split(/\n|;|\.(?=\s|$)|\bpuis\b|\bensuite\b/i)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export const pluralDons = (n) => `${n} ${n > 1 ? "dons" : "don"}`;

// ── Fête du moment ────────────────────────────────────────────────────────
// Un don dicté sans fête va dans la fête EN COURS, sinon dans celle qui
// commence dans 7 jours au plus, sinon nulle part (« Hors fête »).
export const MOMENT_SOON_DAYS = 7;

// fetes : liste de /api/fetes ; moments : dates Hebcal (useFeteMoments).
// null tant que les dates chargent : on ne devine jamais.
export function momentFeteId(fetes, moments) {
  if (!Array.isArray(fetes) || !Array.isArray(moments)) return null;
  const ordered = orderFetes(fetes, moments);
  const now = ordered.find((x) => x.moment.rank === 0);
  if (now) return now.fete.id;
  const soon = ordered.find((x) => x.moment.rank === 1 && Number(x.moment.d) <= MOMENT_SOON_DAYS);
  return soon ? soon.fete.id : null;
}

// ── Lignes de vérification ─────────────────────────────────────────────────
// Une ligne de l'écran Vérification = un item de /poche/parse enrichi des
// choix de l'utilisateur. `doubts` ne contient QUE les doutes encore ouverts.
// keyPrefix : préfixe des lignes d'un complément (« c2- »). Le serveur le pose
// déjà (`key_prefix`) ; s'il ne le fait pas, on le pose ici : deux lignes ne
// partagent jamais la même clé.
function withPrefix(key, prefix) {
  const k = String(key);
  return prefix && !k.startsWith(prefix) ? `${prefix}${k}` : k;
}

export function parsedToDraftItems(parsed, { keyPrefix = "" } = {}) {
  return (parsed?.items || []).map((it, idx) => ({
    key: withPrefix(it.key || `i${idx + 1}`, keyPrefix),
    raw: it.raw || "",
    heard: it.donor?.heard || "",
    donor_id: it.donor?.id || null,
    donor_label: it.donor?.label || "",
    new_donor: null,
    donor_candidates: it.donor_candidates || [],
    amount: it.amount ?? null,
    monthly: !!it.monthly,
    fete_id: it.fete_id || null,
    fete_detail_id: it.fete_detail_id || null,
    motive: it.motive || "Don",
    paid: it.paid === true ? true : it.paid === false ? false : null,
    method: it.method || null,
    paid_at: it.paid_at || todayLocalISO(),
    comment: it.comment || null,
    doubts: (it.doubts || []).map((d) => ({ ...d, options: d.options || [] })),
  }));
}

// Ligne vide créée « à la main » depuis un morceau non compris : tout est à
// renseigner, donc tout est en doute — rien ne part jamais tout seul.
export function blankItem(heard = "") {
  const key = `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
  return {
    key,
    raw: heard,
    heard,
    donor_id: null,
    donor_label: "",
    new_donor: null,
    donor_candidates: [],
    amount: null,
    monthly: false,
    fete_id: null,
    fete_detail_id: null,
    motive: "Don",
    paid: null,
    method: null,
    paid_at: todayLocalISO(),
    comment: null,
    doubts: [
      {
        field: "donor",
        question: "Quel donateur ?",
        options: heard ? [{ value: "new", label: `Nouveau donateur : ${heard}` }] : [],
      },
      { field: "amount", question: "Quel montant ?", options: [] },
      { field: "paid", question: "Payé ou promis ?", options: [] },
    ],
  };
}

export const hasDoubt = (item, field) => (item.doubts || []).some((d) => d.field === field);
export const itemHasDoubts = (item) => (item.doubts || []).length > 0;

export function withoutDoubt(item, ...fields) {
  return { ...item, doubts: (item.doubts || []).filter((d) => !fields.includes(d.field)) };
}

// Une ligne est complète quand elle a un donateur (existant ou nouveau), un
// montant > 0, un statut connu (payé avec mode, ou promis) et plus aucun doute.
export function itemIsComplete(item) {
  const hasDonor = !!item.donor_id || !!(item.new_donor && item.new_donor.last_name?.trim());
  const amount = Number(item.amount);
  const hasAmount = Number.isFinite(amount) && amount > 0;
  const statusKnown = item.paid === false || (item.paid === true && !!item.method);
  return hasDonor && hasAmount && statusKnown && !itemHasDoubts(item);
}

// Nom affiché d'une ligne : donateur choisi, nouveau donateur, ou ce qui a
// été entendu (avec « ? » posé par l'écran).
export function itemDisplayName(item) {
  if (item.donor_id && item.donor_label) return item.donor_label;
  if (item.new_donor) {
    const s = `${item.new_donor.first_name || ""} ${item.new_donor.last_name || ""}`.trim();
    return s || item.heard || "";
  }
  return item.donor_label || item.heard || "";
}

export function itemsTotals(items) {
  let amount = 0;
  let paid = 0;
  let promised = 0;
  for (const it of items) {
    const a = Number(it.amount);
    if (!Number.isFinite(a) || a <= 0) continue;
    amount += a;
    if (it.paid === true) paid += a;
    else if (it.paid === false) promised += a;
  }
  return { count: items.length, amount, paid, promised };
}

// Nom tel que l'analyse l'a entendu (`donor.heard`) : le serveur retient
// l'alias (« Ben Amou » → Michaël Benhamou) pour ne plus jamais le demander.
// Jamais pour une ligne écrite à la main : son « heard » est la phrase entière
// non comprise, pas un nom.
function heardForSave(it) {
  if (String(it.key || "").startsWith("m")) return null;
  const h = String(it.heard || "").trim();
  return h ? h.slice(0, 80) : null;
}

export function itemsToSavePayload(items) {
  return {
    source: "poche",
    items: items.map((it) => ({
      donor_id: it.donor_id || null,
      heard: heardForSave(it),
      new_donor: it.donor_id ? null : it.new_donor || null,
      amount: Number(it.amount),
      motive: it.motive || "Don",
      fete_id: it.fete_id || null,
      fete_detail_id: it.fete_detail_id || null,
      paid: it.paid === true,
      method: it.paid === true ? it.method || null : null,
      paid_at: it.paid_at || todayLocalISO(),
      comment: it.comment ? String(it.comment).trim() || null : null,
    })),
  };
}

// ── Donateurs (résultats de GET /donors) ──────────────────────────────────
export function donorLabel(d) {
  if (!d) return "";
  if (d.donor_type === "entreprise" && d.company_name) return d.company_name;
  return `${d.first_name || ""} ${d.last_name || ""}`.trim() || d.company_name || d.email || "Donateur";
}

// ── Erreurs API lisibles ──────────────────────────────────────────────────
// `detail` est un texte (nos routes) ou une liste (validation Pydantic : on
// ne montre jamais un « [object Object] » ni un message anglais brut).
export function apiErrorMessage(err, fallback = "Une erreur est survenue. Réessayez.") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    const msgs = detail.map((d) => (typeof d === "string" ? d : d?.msg)).filter(Boolean);
    if (msgs.length) return `Données refusées : ${msgs.join(" · ")}`;
  }
  if (!err?.response) return "Pas de connexion. Vérifiez le réseau puis réessayez.";
  if (err.response.status === 403) return "Accès refusé : cette section n'est pas ouverte à votre compte.";
  return fallback;
}

// ── Montant tel qu'on l'écrit en France dans un champ (« 52,5 », « 1800 ») ─
export function amountToInput(n) {
  if (n == null || n === "") return "";
  return String(n).replace(".", ",");
}

// ── Reprise de la dictée : ne pas perdre les corrections déjà faites ───────
// Le texte est réanalysé en entier par le serveur ; on garde la version
// corrigée de chaque ligne dont la phrase (`raw`) n'a pas changé, ainsi que
// les lignes absentes de ce texte : écrites à la main (clé « m… ») et dons
// oubliés ajoutés depuis la vérification (clé « c<N>-… »).
const isExtraKey = (key) => /^(m|c\d+-)/.test(String(key));

export function mergeParsedItems(freshItems, previousItems) {
  const prev = Array.isArray(previousItems) ? previousItems : [];
  if (!prev.length) return freshItems;
  const pool = [...prev];
  // Une phrase de la dictée reprend d'abord SA ligne ; une ligne ajoutée à
  // part (à la main, don oublié) n'est reprise que si la même phrase a été
  // redictée dans le texte : jamais deux lignes pour un même don redicté.
  const find = (raw, extra) => pool.findIndex((p) => p.raw && p.raw === raw && isExtraKey(p.key) === extra);
  const merged = freshItems.map((fresh) => {
    let i = find(fresh.raw, false);
    if (i === -1) i = find(fresh.raw, true);
    if (i === -1) return fresh;
    const [kept] = pool.splice(i, 1);
    return { ...kept, key: fresh.key };
  });
  const extras = pool.filter((p) => isExtraKey(p.key));
  return [...merged, ...extras];
}

// ── Compléter : ajouter un don oublié sans repartir de zéro ────────────────
// Préfixe unique d'un complément (« c1- », « c2- »…) : le compteur vit dans
// le brouillon, et un préfixe déjà porté par une ligne OU par un complément
// déjà ajouté (usedPrefixes) est sauté — même si le brouillon n'a pas pu
// être écrit (stockage plein ou bloqué), deux compléments ne partagent
// jamais un préfixe.
export function nextComplementPrefix(draft, items, usedPrefixes = []) {
  const taken = (items || []).map((it) => String(it.key));
  const used = new Set(usedPrefixes || []);
  let seq = Number(draft?.complement_seq) || 0;
  let prefix;
  do {
    seq += 1;
    prefix = `c${seq}-`;
  } while (used.has(prefix) || taken.some((k) => k.startsWith(prefix)));
  return { prefix, seq };
}

// Les lignes du complément vont À LA SUITE : les lignes existantes (et leurs
// corrections) ne bougent pas, et une clé déjà prise n'écrase jamais rien.
export function appendParsedItems(currentItems, freshItems) {
  const current = Array.isArray(currentItems) ? currentItems : [];
  const taken = new Set(current.map((it) => it.key));
  const added = (freshItems || []).map((it) => {
    let key = it.key;
    for (let n = 2; taken.has(key); n += 1) key = `${it.key}-${n}`;
    taken.add(key);
    return key === it.key ? it : { ...it, key };
  });
  return { items: [...current, ...added], added };
}

// « Pas compris » d'un complément : ils ne sont pas dans le texte de la
// dictée, on les garde à part pour qu'une reprise de la dictée ne les perde
// pas (ceux déjà traités sont retirés par `dropHandledUnparsed`).
export function rememberExtraUnparsed(key, list) {
  if (!Array.isArray(list) || !list.length) return;
  const d = loadDraft(key) || {};
  const prev = Array.isArray(d.extra_unparsed) ? d.extra_unparsed : [];
  saveDraft(key, { extra_unparsed: [...prev, ...list] });
}

// ── Comment la dictée a été comprise : « IA » ou « règles » ────────────────
// parses : [{ engine, ai_cost_cents }] (dictée puis compléments). null quand
// le serveur ne l'a pas dit (ancienne version) : l'écran n'affiche alors rien.
export function engineSummary(parses) {
  const list = (parses || []).filter((p) => p && (p.engine === "ia" || p.engine === "regles"));
  if (!list.length) return null;
  const ia = list.filter((p) => p.engine === "ia");
  if (!ia.length) return { engine: "regles", costCents: null };
  const cost = ia.reduce((sum, p) => {
    const c = Number(p.ai_cost_cents);
    return sum + (Number.isFinite(c) && c > 0 ? c : 0);
  }, 0);
  return { engine: "ia", costCents: cost };
}

// « ≈ 0,08 centime » (coût estimé : jetons × prix du fournisseur).
export function aiCostLabel(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n) || n <= 0) return "coût non mesuré";
  if (n < 0.01) return "moins de 0,01 centime";
  const s = n.toLocaleString("fr-FR", { maximumFractionDigits: 2 });
  return `≈ ${s} ${n >= 2 ? "centimes" : "centime"}`;
}

// Morceaux « pas compris » déjà traités (ignorés ou ajoutés à la main) : ils
// ne doivent pas réapparaître après une reprise de la dictée.
export function rememberHandledUnparsed(key, text) {
  const d = loadDraft(key) || {};
  const list = Array.isArray(d.handled_unparsed) ? d.handled_unparsed : [];
  if (!list.includes(text)) saveDraft(key, { handled_unparsed: [...list, text] });
}

export function dropHandledUnparsed(unparsed, draft) {
  const handled = Array.isArray(draft?.handled_unparsed) ? draft.handled_unparsed : [];
  if (!handled.length) return unparsed;
  const pool = [...handled];
  return unparsed.filter((txt) => {
    const i = pool.indexOf(txt);
    if (i === -1) return true;
    pool.splice(i, 1);
    return false;
  });
}

// ── Toasts : en HAUT de l'écran, pour ne jamais passer sous la barre du bas ─
const TOAST_OPTS = { position: "top-center", duration: 3500 };
export const pocheToast = {
  success: (msg) => toast.success(msg, TOAST_OPTS),
  error: (msg) => toast.error(msg, { ...TOAST_OPTS, duration: 5000 }),
  info: (msg) => toast(msg, TOAST_OPTS),
};
